import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import colorPalettes from '../../data/colorPalettes';

const ColorPalette = () => {
  const navigate = useNavigate();
  
  // 状态管理
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('全部');
  
  // 获取所有主题
  const themes = useMemo(() => {
    const uniqueThemes = ['全部', ...new Set(colorPalettes.map(palette => palette.theme))];
    return uniqueThemes;
  }, []);
  
  // 过滤配色方案
  const filteredPalettes = useMemo(() => {
    return colorPalettes.filter(palette => {
      const matchesSearch = palette.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          palette.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTheme = selectedTheme === '全部' || palette.theme === selectedTheme;
      return matchesSearch && matchesTheme;
    });
  }, [searchTerm, selectedTheme]);
  
  // 复制颜色代码
  const copyColorCode = (color) => {
    navigator.clipboard.writeText(color)
      .then(() => {
        // 可以添加复制成功提示
        console.log('颜色代码已复制:', color);
      })
      .catch(err => {
        console.error('复制失败:', err);
      });
  };
  
  // 应用到计算器
  const applyToCalculator = (palette) => {
    // 将配色方案数据转换为适合计算器使用的格式
    const calculatorData = {
      colors: palette.colors,
      name: palette.name,
      suitableCrystals: palette.suitableCrystals
    };
    
    // 跳转到计算器页面，并将配色方案数据作为查询参数传递
    navigate('/calculators/bracelet-calculator', {
      state: { colorPalette: calculatorData }
    });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-center mb-8 text-blue-800">
          手链配色灵感库
        </h1>
        
        {/* 搜索和筛选区域 */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-blue-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 搜索框 */}
            <div className="md:col-span-2">
              <input
                type="text"
                placeholder="搜索配色方案..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>
            
            {/* 主题筛选 */}
            <div>
              <select
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                {themes.map((theme) => (
                  <option key={theme} value={theme}>
                    {theme}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* 结果统计 */}
          <div className="mt-4 text-gray-600">
            共找到 {filteredPalettes.length} 种配色方案
          </div>
        </div>
        
        {/* 配色方案列表 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPalettes.map((palette) => (
            <div key={palette.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-blue-100 hover:shadow-xl transition-shadow duration-300">
              {/* 配色方案图片 */}
              <div className="h-48 overflow-hidden relative">
                <img
                  src={palette.image}
                  alt={palette.name}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
                {/* 主题标签 */}
                <div className="absolute top-4 left-4 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {palette.theme}
                </div>
              </div>
              
              {/* 配色方案信息 */}
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-2 text-blue-700">
                  {palette.name}
                </h2>
                <p className="text-gray-600 mb-4 line-clamp-2">{palette.description}</p>
                
                {/* 适合的水晶 */}
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">适合水晶：</p>
                  <div className="flex flex-wrap gap-2">
                    {palette.suitableCrystals.map((crystal, index) => (
                      <span
                        key={index}
                        className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs border border-blue-200"
                      >
                        {crystal}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* 颜色展示 */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {palette.colors.map((color, index) => (
                    <div
                      key={index}
                      className="relative group"
                    >
                      <div
                        className="w-12 h-12 rounded-full border-2 border-white shadow-md cursor-pointer hover:scale-110 transition-transform duration-200"
                        style={{ backgroundColor: color }}
                        onClick={() => copyColorCode(color)}
                        title={`点击复制颜色代码: ${color}`}
                      ></div>
                      {/* 颜色代码提示 */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                        {color}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* 操作按钮 */}
                <button 
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                  onClick={() => applyToCalculator(palette)}
                >
                  应用到计算器
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {/* 空状态 */}
        {filteredPalettes.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl shadow-lg border border-blue-100">
            <p className="text-gray-500 text-lg">未找到匹配的配色方案</p>
            <p className="text-gray-400 mt-2">请尝试调整搜索条件或筛选主题</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorPalette;